/**
 * Distribution Calculator - Shared calculation logic for TRONC distribution
 * This file mirrors the backend DistributionCalculator.cs logic
 * Simplified to 3 core strategies with universal options.
 */

// Distribution types matching backend enum (simplified)
export const DistributionType = {
  Equal: 0,
  RoleScore: 1,
  ByDepartment: 2,
  // Legacy aliases for backward compatibility
  ByJobRole: 1,
  ByJobRoleScore: 1
};

// Legacy type mappings for backward compatibility
const LEGACY_TYPE_MAP = {
  1: 1, // Old ByJobRole -> RoleScore (use same calculation)
  2: 1, // Old ByJobRoleScore -> RoleScore
  3: 2, // Old ByDepartment -> ByDepartment
  4: 0, // Old ByHoursAndJobRole -> Equal (use hour factor option)
  5: 0, // Old HoursWeighted -> Equal (use hour factor option)
  6: 0, // Old RolePool -> Equal
  7: 0, // Old MinimumFloor -> Equal (use minimum floor option)
  8: 0, // Old SeniorityWeighted -> Equal (use seniority factor option)
};

// Tolerance for percentage sum validation
export const PERCENTAGE_TOLERANCE = 0.01;
export const REQUIRED_TOTAL_PERCENTAGE = 100;

// ============================================================================
// EQUAL DISTRIBUTION STRATEGY
// ============================================================================

const EqualDistributionStrategy = {
  type: DistributionType.Equal,

  getDescription: () =>
    'Distributes TRONC equally among all eligible employees regardless of role or hours worked.',

  validate: (rule) => {
    const errors = [];
    if (!rule.name || rule.name.trim() === '') {
      errors.push('Rule name is required.');
    }
    return { isValid: errors.length === 0, errors };
  },

  calculate: (context) => {
    const { troncAmount, employees } = context;
    if (!employees || employees.length === 0) return [];

    const distributions = [];
    const equalAmount = Math.round((troncAmount / employees.length) * 100) / 100;
    let remaining = troncAmount;

    employees.forEach((emp, index) => {
      let amount;
      if (index === employees.length - 1) {
        amount = Math.round(remaining * 100) / 100;
      } else {
        amount = equalAmount;
        remaining -= amount;
      }

      distributions.push({
        employeeId: emp.id,
        employeeName: emp.name || emp.fullName || `${emp.firstName} ${emp.lastName}`,
        jobRoleName: emp.jobRoleName || emp.role || emp.jobTitle,
        amount,
        percentage: troncAmount > 0 ? (amount / troncAmount) * 100 : 0,
        calculation: { rolePercentage: 0, points: 0, weightedPoints: 0 }
      });
    });

    return distributions;
  }
};

// ============================================================================
// ROLE SCORE DISTRIBUTION STRATEGY
// ============================================================================

const RoleScoreDistributionStrategy = {
  type: DistributionType.RoleScore,

  getDescription: () =>
    'Distributes TRONC based on each job role\'s configured TroncScore. ' +
    'Allocations must sum to 100%. ' +
    'Percentages are automatically calculated based on the relative scores of each role.',

  validate: (rule) => {
    const errors = [];
    if (!rule.name || rule.name.trim() === '') {
      errors.push('Rule name is required.');
    }
    
    // For ByJobRole type with allocations, validate them
    const allocations = (rule.allocations || []).filter(a => a.jobRoleId);
    
    if (allocations.length > 0) {
      // Check for negative percentages
      const hasNegative = allocations.some(a => (a.percentage || 0) < 0);
      if (hasNegative) {
    errors.push('Allocation percentages cannot be negative.');
    }
      
      // Check for duplicates
      const roleIds = allocations.map(a => a.jobRoleId);
      const uniqueRoleIds = new Set(roleIds);
      if (roleIds.length !== uniqueRoleIds.size) {
        errors.push('Cannot have duplicate job role allocations.');
      }
  
      // Check sum to 100%
      const totalPercentage = allocations.reduce((sum, a) => sum + (a.percentage || 0), 0);
      if (Math.abs(totalPercentage - REQUIRED_TOTAL_PERCENTAGE) > PERCENTAGE_TOLERANCE) {
  errors.push(`Allocations must sum to exactly 100%. Current total: ${totalPercentage.toFixed(2)}%`);
      }
    }
    
    return { isValid: errors.length === 0, errors };
  },

  calculate: (context) => {
    const { troncAmount, employees, jobRoles, rule } = context;
    if (!employees || employees.length === 0) return [];

    const distributions = [];
    
    // Check if rule has manual allocations
    const allocations = (rule?.allocations || []).filter(a => a.jobRoleId);
    
  if (allocations.length > 0) {
      // Use manual allocations - ByJobRole style
      const allocationMap = allocations.reduce((acc, a) => ({ 
        ...acc, 
        [a.jobRoleId]: a.percentage 
      }), {});
      
  // Group employees by role
      const employeesByRole = {};
      employees.forEach(emp => {
        const roleId = emp.jobRoleId || emp.primaryJobRoleId;
        if (allocationMap[roleId] !== undefined) {
          if (!employeesByRole[roleId]) {
     employeesByRole[roleId] = [];
          }
          employeesByRole[roleId].push(emp);
        }
      });
      
    // Distribute based on role allocations
      Object.entries(allocationMap).forEach(([roleId, percentage]) => {
        const roleEmployees = employeesByRole[roleId] || [];
        if (roleEmployees.length === 0) return;
    
        const roleAmount = Math.round((troncAmount * percentage / 100) * 100) / 100;
const amountPerEmployee = Math.round((roleAmount / roleEmployees.length) * 100) / 100;
        let roleRemaining = roleAmount;
        
     roleEmployees.forEach((emp, index) => {
          let amount;
          if (index === roleEmployees.length - 1) {
            amount = Math.round(roleRemaining * 100) / 100;
          } else {
         amount = amountPerEmployee;
         roleRemaining -= amount;
          }
          
          distributions.push({
          employeeId: emp.id,
     employeeName: emp.name || emp.fullName || `${emp.firstName} ${emp.lastName}`,
  jobRoleName: emp.jobRoleName || emp.role || emp.jobTitle,
   amount,
        percentage: troncAmount > 0 ? (amount / troncAmount) * 100 : 0,
            calculation: { rolePercentage: percentage, points: 0, weightedPoints: 0 }
          });
});
      });
      
      return distributions;
    }

    // No allocations - use automatic score-based distribution
    // Create a map of role scores
    const roleScores = {};
(jobRoles || []).forEach(role => {
      if (role.isEligibleForTronc && (role.troncScore || role.troncPoints) > 0) {
        roleScores[role.id] = role.troncScore || role.troncPoints;
      }
    });

    // Calculate total score across all eligible employees
    let totalScore = 0;
    const employeeScores = {};

    employees.forEach(emp => {
      const roleId = emp.jobRoleId || emp.primaryJobRoleId;
      const score = roleScores[roleId] || emp.troncScore || 0;
      
   if (score > 0) {
        employeeScores[emp.id] = score;
     totalScore += score;
      }
    });

    // If no scores, fallback to equal distribution
  if (totalScore === 0) {
      return EqualDistributionStrategy.calculate(context);
    }

    // Calculate percentage for each job role (for display)
    const rolePercentages = {};
    const employeesByRole = {};
    employees.forEach(emp => {
      const roleId = emp.jobRoleId || emp.primaryJobRoleId;
      if (!employeesByRole[roleId]) {
        employeesByRole[roleId] = [];
      }
      employeesByRole[roleId].push(emp);
    });

    Object.entries(roleScores).forEach(([roleId, score]) => {
      if (employeesByRole[roleId]) {
    const roleEmployeeCount = employeesByRole[roleId].length;
  const roleTotalScore = score * roleEmployeeCount;
        rolePercentages[roleId] = (roleTotalScore / totalScore) * 100;
      }
    });

    // Distribute based on individual scores
    let distributed = 0;
    const eligibleEmployees = employees.filter(e => employeeScores[e.id] !== undefined);

    eligibleEmployees.forEach((emp, index) => {
      const score = employeeScores[emp.id] || 0;
      let amount;

      if (index === eligibleEmployees.length - 1) {
    amount = Math.round((troncAmount - distributed) * 100) / 100;
      } else {
        amount = Math.round((score / totalScore * troncAmount) * 100) / 100;
        distributed += amount;
      }

    const roleId = emp.jobRoleId || emp.primaryJobRoleId;
      const rolePercentage = rolePercentages[roleId] || 0;

      distributions.push({
     employeeId: emp.id,
  employeeName: emp.name || emp.fullName || `${emp.firstName} ${emp.lastName}`,
        jobRoleName: emp.jobRoleName || emp.role || emp.jobTitle,
        amount,
        percentage: troncAmount > 0 ? (amount / troncAmount) * 100 : 0,
        calculation: {
          rolePercentage,
 points: score,
        weightedPoints: score
        }
      });
    });

    // Add zero-amount entries for ineligible employees
    employees.filter(e => employeeScores[e.id] === undefined).forEach(emp => {
   distributions.push({
        employeeId: emp.id,
        employeeName: emp.name || emp.fullName || `${emp.firstName} ${emp.lastName}`,
        jobRoleName: emp.jobRoleName || emp.role || emp.jobTitle,
     amount: 0,
        percentage: 0,
        calculation: { rolePercentage: 0, points: 0, weightedPoints: 0 }
      });
    });

    return distributions;
  }
};

// ============================================================================
// BY DEPARTMENT DISTRIBUTION STRATEGY
// ============================================================================

const ByDepartmentDistributionStrategy = {
  type: DistributionType.ByDepartment,

  getDescription: () =>
    'Distributes TRONC based on department allocations (FOH, BOH, Admin, Management). ' +
    'Within each department, the allocated amount is split equally among employees.',

  validate: (rule) => {
    const errors = [];

if (!rule.name || rule.name.trim() === '') {
      errors.push('Rule name is required.');
    }

    const deptAllocations = (rule.allocations || []).filter(a => a.department);
    
    if (deptAllocations.length === 0) {
    errors.push('ByDepartment distribution requires at least one department allocation.');
      return { isValid: false, errors };
    }

    const totalPercentage = deptAllocations.reduce((sum, a) => sum + (a.percentage || 0), 0);
    
    if (Math.abs(totalPercentage - REQUIRED_TOTAL_PERCENTAGE) > PERCENTAGE_TOLERANCE) {
      errors.push(`Department allocations must sum to exactly 100%. Current total: ${totalPercentage.toFixed(2)}%`);
    }

    return { isValid: errors.length === 0, errors };
  },

  calculate: (context) => {
    const { troncAmount, employees, rule, jobRoles } = context;
    if (!employees || employees.length === 0) return [];

    const allocations = (rule.allocations || [])
      .filter(a => a.department)
      .reduce((acc, a) => ({ ...acc, [a.department]: a.percentage }), {});

    if (Object.keys(allocations).length === 0) return [];

    const distributions = [];

    // Group employees by department
    const employeesByDepartment = {};
    employees.forEach(emp => {
      const dept = getEmployeeDepartment(emp, jobRoles);
      if (!employeesByDepartment[dept]) {
        employeesByDepartment[dept] = [];
    }
      employeesByDepartment[dept].push(emp);
    });

    // Distribute based on department allocations
    Object.entries(allocations).forEach(([dept, percentage]) => {
      const deptEmployees = employeesByDepartment[dept] || [];
      if (deptEmployees.length === 0) return;

      const deptAmount = Math.round((troncAmount * percentage / 100) * 100) / 100;
      const amountPerEmployee = Math.round((deptAmount / deptEmployees.length) * 100) / 100;
      let deptRemaining = deptAmount;

      deptEmployees.forEach((emp, index) => {
    let amount;
     if (index === deptEmployees.length - 1) {
       amount = Math.round(deptRemaining * 100) / 100;
        } else {
          amount = amountPerEmployee;
          deptRemaining -= amount;
 }

        distributions.push({
          employeeId: emp.id,
    employeeName: emp.name || emp.fullName || `${emp.firstName} ${emp.lastName}`,
      jobRoleName: emp.jobRoleName || emp.role || emp.jobTitle,
          amount,
          percentage: troncAmount > 0 ? (amount / troncAmount) * 100 : 0,
          calculation: { rolePercentage: percentage, points: 0, weightedPoints: 0 }
        });
      });
    });

    // Add zero entries for employees in departments not in allocations
    const includedIds = new Set(distributions.map(d => d.employeeId));
    employees.filter(e => !includedIds.has(e.id)).forEach(emp => {
      distributions.push({
        employeeId: emp.id,
        employeeName: emp.name || emp.fullName || `${emp.firstName} ${emp.lastName}`,
        jobRoleName: emp.jobRoleName || emp.role || emp.jobTitle,
        amount: 0,
 percentage: 0,
        calculation: { rolePercentage: 0, points: 0, weightedPoints: 0 }
      });
    });

    return distributions;
  }
};

/**
 * Determines department for an employee based on job role
 * Returns the actual department from the job role - no inference or guessing
 */
function getEmployeeDepartment(emp, jobRoles) {
  const roleId = emp.jobRoleId || emp.primaryJobRoleId;
  const role = (jobRoles || []).find(r => r.id === roleId);
  
  // Return the actual department from the job role - no inference
  return role?.department || null;
}

// ============================================================================
// UNIVERSAL OPTIONS CALCULATOR
// ============================================================================

/**
 * Applies universal options (Minimum Floor, Hour Factor, Seniority Factor)
 * to the base distribution
 */
function applyUniversalOptions(context, distributions) {
  let result = [...distributions];
  const { rule, troncAmount, employees, clockRecords } = context;

  // Apply Hour Factor
  if (rule.enableHourFactor && rule.hourFactorWeight > 0) {
    result = applyHourFactor(result, employees, clockRecords, troncAmount, rule.hourFactorWeight);
  }

  // Apply Seniority Factor
  if (rule.enableSeniorityFactor && rule.seniorityFactorWeight > 0) {
result = applySeniorityFactor(result, employees, troncAmount, rule.seniorityFactorWeight, rule.seniorityBands);
  }

  // Apply Minimum Floor (last, after all weighting)
  if (rule.enableMinimumFloor && rule.minimumFloorAmount > 0) {
    result = applyMinimumFloor(result, troncAmount, rule.minimumFloorAmount);
  }

  return result;
}

function applyHourFactor(distributions, employees, clockRecords = [], troncAmount, weight) {
  const employeeHours = {};
  
  employees.forEach(emp => {
    const hours = (clockRecords || [])
      .filter(c => c.employeeId === emp.id)
      .reduce((sum, c) => sum + (c.hoursWorked || 0), 0);
    employeeHours[emp.id] = hours || (emp.contractedHoursPerWeek || 40) / 5;
  });

  const totalHours = Object.values(employeeHours).reduce((sum, h) => sum + h, 0);
  if (totalHours === 0) return distributions;

  let distributed = 0;
  return distributions.map((d, index) => {
 const hours = employeeHours[d.employeeId] || 0;
    const hoursShare = (hours / totalHours) * troncAmount;
    
    let newAmount;
    if (index === distributions.length - 1) {
      newAmount = Math.round((troncAmount - distributed) * 100) / 100;
    } else {
      newAmount = Math.round((d.amount * (1 - weight) + hoursShare * weight) * 100) / 100;
      distributed += newAmount;
    }

    return {
      ...d,
      amount: newAmount,
 percentage: troncAmount > 0 ? (newAmount / troncAmount) * 100 : 0,
      calculation: {
     ...d.calculation,
        hoursWorked: hours,
        hourFactorMultiplier: hours / (totalHours / distributions.length)
 }
    };
  });
}

function applySeniorityFactor(distributions, employees, troncAmount, weight, bands) {
  const defaultBands = [
    { minMonths: 0, maxMonths: 6, multiplier: 1.0 },
    { minMonths: 6, maxMonths: 12, multiplier: 1.2 },
    { minMonths: 12, maxMonths: 24, multiplier: 1.4 },
{ minMonths: 24, maxMonths: null, multiplier: 1.6 }
  ];

  const seniorityBands = bands?.length > 0 ? bands : defaultBands;

  const employeeMultipliers = {};
  employees.forEach(emp => {
    const months = emp.monthsOfService ?? calculateMonthsOfService(emp.startDate);
    employeeMultipliers[emp.id] = {
      multiplier: getSeniorityMultiplier(months, seniorityBands),
  months
    };
  });

  const totalMultipliers = Object.values(employeeMultipliers).reduce((sum, e) => sum + e.multiplier, 0);
  if (totalMultipliers === 0) return distributions;

  let distributed = 0;
  return distributions.map((d, index) => {
    const { multiplier, months } = employeeMultipliers[d.employeeId] || { multiplier: 1, months: 0 };
    const seniorityShare = (multiplier / totalMultipliers) * troncAmount;
    
    let newAmount;
    if (index === distributions.length - 1) {
      newAmount = Math.round((troncAmount - distributed) * 100) / 100;
    } else {
      newAmount = Math.round((d.amount * (1 - weight) + seniorityShare * weight) * 100) / 100;
      distributed += newAmount;
    }

    return {
      ...d,
      amount: newAmount,
      percentage: troncAmount > 0 ? (newAmount / troncAmount) * 100 : 0,
      calculation: {
     ...d.calculation,
        seniorityMultiplier: multiplier,
  monthsOfService: months
      }
    };
  });
}

function applyMinimumFloor(distributions, troncAmount, minimumFloor) {
  const employeeCount = distributions.length;
  const totalRequired = minimumFloor * employeeCount;

  // If not enough for everyone to get minimum, split equally
  if (totalRequired > troncAmount) {
 const equalAmount = Math.round((troncAmount / employeeCount) * 100) / 100;
    let remaining = troncAmount;
    return distributions.map((d, index) => ({
      ...d,
      amount: index === employeeCount - 1 ? Math.round(remaining * 100) / 100 : equalAmount,
      percentage: troncAmount > 0 ? (equalAmount / troncAmount) * 100 : 0
    })).map((d, i, arr) => {
      if (i < arr.length - 1) remaining -= d.amount;
    return d;
    });
  }

  // Guarantee minimum, distribute remainder proportionally
  const remainder = troncAmount - totalRequired;
  const originalTotal = distributions.reduce((sum, d) => sum + d.amount, 0);
  
  let distributed = 0;
  return distributions.map((d, index) => {
    let newAmount;
    if (index === distributions.length - 1) {
 newAmount = Math.round((troncAmount - distributed) * 100) / 100;
    } else {
      const proportionalShare = originalTotal > 0 
      ? (d.amount / originalTotal) * remainder 
 : remainder / distributions.length;
      newAmount = Math.round((minimumFloor + proportionalShare) * 100) / 100;
   distributed += newAmount;
    }

    return {
      ...d,
      amount: newAmount,
      percentage: troncAmount > 0 ? (newAmount / troncAmount) * 100 : 0
    };
  });
}

function getSeniorityMultiplier(months, bands) {
  for (const band of bands.sort((a, b) => a.minMonths - b.minMonths)) {
if (months >= band.minMonths && (band.maxMonths === null || months < band.maxMonths)) {
   return band.multiplier;
    }
  }
  return 1.0;
}

function calculateMonthsOfService(startDate) {
  if (!startDate) return 0;
  const start = new Date(startDate);
  const now = new Date();
  return Math.max(0, (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth()));
}

// ============================================================================
// STRATEGY REGISTRY
// ============================================================================

const strategies = {
  [DistributionType.Equal]: EqualDistributionStrategy,
  [DistributionType.RoleScore]: RoleScoreDistributionStrategy,
  [DistributionType.ByDepartment]: ByDepartmentDistributionStrategy
};

/**
 * Gets the strategy for a distribution type
 */
export function getStrategy(type) {
  const normalizedType = parseDistributionType(type);
  return strategies[normalizedType] || EqualDistributionStrategy;
}

/**
 * Parses distribution type string/number to new enum value
 */
export function parseDistributionType(type) {
  if (typeof type === 'number') {
    // Handle legacy type mappings
    if (type > 2) {
      return LEGACY_TYPE_MAP[type] ?? 0;
    }
    return type;
  }
  
  const typeStr = (type || '').toLowerCase();
  switch (typeStr) {
  case 'equal':
    return DistributionType.Equal;
    case 'rolescore':
    case 'byjobrolesco':
    case 'byjobrolescore':
      return DistributionType.RoleScore;
    case 'bydepartment':
    return DistributionType.ByDepartment;
    // Legacy mappings
    case 'byjobrole':
      return DistributionType.RoleScore;
    case 'hoursweighted':
    case 'byhoursandjobrole':
    case 'rolepool':
    case 'minimumfloor':
    case 'seniorityweighted':
      return DistributionType.Equal;
    default:
    return parseInt(type) || DistributionType.Equal;
  }
}

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Validates a distribution rule
 */
export function validateRule(rule) {
  const errors = [];
  
  // Always check rule name first
  if (!rule.name || rule.name.trim() === '') {
    errors.push('Rule name is required.');
  }
  
  const ruleType = parseDistributionType(rule.type);
  const allocations = (rule.allocations || []).filter(a => a.jobRoleId);
  
  // Logic: If there are allocations provided, validate them regardless of type
  // If it's a RoleScore type (1) with NO allocations, that's automatic score-based (valid)
 // If it's a RoleScore type (1) WITH allocations, that's manual ByJobRole (validate allocations)
  
  const isRoleScoreType = (ruleType === 1 || ruleType === DistributionType.RoleScore);
  const hasAllocations = allocations.length > 0;
  
  if (isRoleScoreType && hasAllocations) {
    // Manual ByJobRole with allocations - validate them
    // Check for negative percentages
    const hasNegative = allocations.some(a => (a.percentage || 0) < 0);
    if (hasNegative) {
      errors.push('Allocation percentages cannot be negative.');
    }
    
    // Check for duplicates
    const roleIds = allocations.map(a => a.jobRoleId);
    const uniqueRoleIds = new Set(roleIds);
    if (roleIds.length !== uniqueRoleIds.size) {
      errors.push('Cannot have duplicate job role allocations.');
    }

    // Check sum to 100%
    const totalPercentage = allocations.reduce((sum, a) => sum + (a.percentage || 0), 0);
    if (Math.abs(totalPercentage - REQUIRED_TOTAL_PERCENTAGE) > PERCENTAGE_TOLERANCE) {
      errors.push(`Allocations must sum to exactly 100%. Current total: ${totalPercentage.toFixed(2)}%`);
 }
  } else if (!isRoleScoreType) {
    // For other types (Equal, ByDepartment), use strategy validation
    // But skip name validation since we already checked it
    const strategy = getStrategy(ruleType);
    const strategyResult = strategy.validate(rule);
    errors.push(...strategyResult.errors.filter(e => !e.toLowerCase().includes('name')));
  }
  // If isRoleScoreType and no allocations, it's automatic ByJobRoleScore - valid (no extra validation)

  // Validate universal options
  if (rule.enableMinimumFloor) {
    if (!rule.minimumFloorAmount || rule.minimumFloorAmount <= 0) {
    errors.push('Minimum floor amount must be greater than zero when enabled.');
  }
}
  if (rule.enableHourFactor) {
    if (rule.hourFactorWeight < 0 || rule.hourFactorWeight > 1) {
      errors.push('Hour factor weight must be between 0 and 1.');
    }
  }
  if (rule.enableSeniorityFactor) {
    if (rule.seniorityFactorWeight < 0 || rule.seniorityFactorWeight > 1) {
      errors.push('Seniority factor weight must be between 0 and 1.');
    }
  }

  return { isValid: errors.length === 0, errors };
}

/**
 * Main distribution calculation function
 */
export function calculateDistribution(rule, totalAmount, employees, jobRoles = [], clockRecords = []) {
  const strategy = getStrategy(rule.type);
  const context = {
    troncAmount: totalAmount,
    employees,
    jobRoles,
    clockRecords,
    rule
  };
  
  // Calculate base distribution
  let distributions = strategy.calculate(context);
  
  // Apply universal options
  if (rule.enableHourFactor || rule.enableSeniorityFactor || rule.enableMinimumFloor) {
    distributions = applyUniversalOptions(context, distributions);
  }
  
  return distributions;
}

/**
 * Validates that distribution total equals the TRONC amount
 */
export function validateDistributionTotal(totalAmount, distributions) {
  const distributedTotal = distributions.reduce((sum, d) => sum + (d.amount || 0), 0);
  const difference = totalAmount - distributedTotal;
  return {
    isValid: Math.abs(difference) < 0.01,
    difference
  };
}

/**
 * Calculates auto-generated allocations for RoleScore type
 */
export function calculateScoreBasedAllocations(jobRoles) {
const eligibleRoles = (jobRoles || []).filter(
    r => r.isEligibleForTronc && (r.troncScore || r.troncPoints || 0) > 0
  );

  const totalScore = eligibleRoles.reduce(
 (sum, r) => sum + (r.troncScore || r.troncPoints || 0), 
    0
  );

  if (totalScore === 0) {
    return [];
  }

  return eligibleRoles
    .map(r => ({
      jobRoleId: r.id,
      jobRoleName: r.name,
   troncScore: r.troncScore || r.troncPoints,
      percentage: Math.round(((r.troncScore || r.troncPoints) / totalScore * 100) * 100) / 100
    }))
    .sort((a, b) => b.percentage - a.percentage);
}

/**
 * Gets description for a distribution type
 */
export function getDistributionTypeDescription(type) {
  const strategy = getStrategy(type);
  return strategy.getDescription();
}

/**
 * Gets all distribution types with descriptions
 */
export function getAllDistributionTypes() {
  return [
    { type: 0, name: 'Equal', description: EqualDistributionStrategy.getDescription() },
    { type: 1, name: 'RoleScore', description: RoleScoreDistributionStrategy.getDescription() },
    { type: 2, name: 'ByDepartment', description: ByDepartmentDistributionStrategy.getDescription() }
  ];
}

/**
 * Gets default seniority bands
 */
export function getDefaultSeniorityBands() {
  return [
    { minMonths: 0, maxMonths: 6, multiplier: 1.0, name: 'Probationary' },
    { minMonths: 6, maxMonths: 12, multiplier: 1.2, name: 'Standard' },
    { minMonths: 12, maxMonths: 24, multiplier: 1.4, name: 'Established' },
    { minMonths: 24, maxMonths: null, multiplier: 1.6, name: 'Senior' }
  ];
}

// Legacy exports for backward compatibility
export const calculateEqual = (totalAmount, employees) => 
  EqualDistributionStrategy.calculate({ troncAmount: totalAmount, employees });

export const calculateByRoleScore = (totalAmount, employees, jobRoles) =>
  RoleScoreDistributionStrategy.calculate({ 
    troncAmount: totalAmount, 
    employees, 
    jobRoles 
  });

// Alias for old name
export const calculateByJobRoleScore = calculateByRoleScore;

// ByJobRole with manual allocations
export const calculateByJobRole = (totalAmount, employees, allocations = []) => {
  if (!allocations || allocations.length === 0) {
    return [];
  }
  
  return RoleScoreDistributionStrategy.calculate({
    troncAmount: totalAmount,
    employees,
    rule: { allocations }
  });
};

export default {
  DistributionType,
  PERCENTAGE_TOLERANCE,
  REQUIRED_TOTAL_PERCENTAGE,
  getStrategy,
  parseDistributionType,
  validateRule,
  calculateDistribution,
  validateDistributionTotal,
  calculateScoreBasedAllocations,
  getDistributionTypeDescription,
  getAllDistributionTypes,
  getDefaultSeniorityBands,
  calculateEqual,
  calculateByRoleScore,
  calculateByJobRoleScore,
  calculateByJobRole
};
